import { useState, useCallback, useMemo } from 'react';

import { BsPlus, BsTrash } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import CollectionToolbar from '#globalComponents/CollectionView/CollectionToolbar';
import CollectionView from '#globalComponents/CollectionView/CollectionView';
import useCollectionSearch from '#globalComponents/CollectionView/useCollectionSearch';
import useCollectionViewMode from '#globalComponents/CollectionView/useCollectionViewMode';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import Page from '#globalComponents/Page/Page';
import PageHeader from '#globalComponents/PageHeader/PageHeader';
import { useErrorStore } from '#lib/stores/errorStore';

import {
	useCreateEmailTemplate,
	useUpdateEmailTemplate,
	useDeleteEmailTemplate,
	useUploadEmailTemplateAttachment,
	useDeleteEmailTemplateAttachment,
} from '../../models/emailTemplates/mutations';
import { useEmailTemplates } from '../../models/emailTemplates/queries';

import EmailTemplateEditorModal from './EmailTemplateEditorModal';

const getSearchText = (template) => `${template.name} ${template.emailSubject || ''}`;

const EmailTemplatesPage = () => {
	const { data: templates = [], isLoading } = useEmailTemplates();
	const createMutation = useCreateEmailTemplate();
	const updateMutation = useUpdateEmailTemplate();
	const deleteMutation = useDeleteEmailTemplate();
	const uploadAttachmentMutation = useUploadEmailTemplateAttachment();
	const deleteAttachmentMutation = useDeleteEmailTemplateAttachment();
	const showError = useErrorStore((s) => s.showError);

	const [editorOpen, setEditorOpen] = useState(false);
	const [editingTemplate, setEditingTemplate] = useState(null);
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [viewMode, setViewMode] = useCollectionViewMode('emailTemplates');
	const { search, setSearch, filteredItems: filteredTemplates } = useCollectionSearch(templates, getSearchText);

	const handleCreate = useCallback(() => {
		setEditingTemplate(null);
		setEditorOpen(true);
	}, []);

	const handleEdit = useCallback((tpl) => {
		setEditingTemplate(tpl);
		setEditorOpen(true);
	}, []);

	const handleSave = useCallback(
		async ({ attachmentFile, removeAttachment, ...payload }) => {
			let saved = editingTemplate;
			try {
				if (editingTemplate) {
					const res = await updateMutation.mutateAsync({ id: editingTemplate.id, payload });
					saved = res.template ?? editingTemplate;
				} else {
					const res = await createMutation.mutateAsync(payload);
					saved = res.template;
				}
			} catch (err) {
				showError({ error: err, title: 'Template konnte nicht gespeichert werden' });
				return;
			}

			// Attachment step runs after the template exists (deferred upload — a new template has no id
			// before create). On failure the global mutation toast reports it; the modal stays open and
			// switches to editing the saved template so a retry cannot create a duplicate.
			try {
				if (attachmentFile && saved?.id) {
					await uploadAttachmentMutation.mutateAsync({ templateId: saved.id, file: attachmentFile });
				} else if (removeAttachment && saved?.id) {
					await deleteAttachmentMutation.mutateAsync(saved.id);
				}
			} catch {
				setEditingTemplate(saved);
				return;
			}

			setEditorOpen(false);
			setEditingTemplate(null);
		},
		[
			editingTemplate,
			createMutation,
			updateMutation,
			uploadAttachmentMutation,
			deleteAttachmentMutation,
			showError,
		],
	);

	const renameTemplate = updateMutation.mutateAsync;
	const items = useMemo(
		() =>
			filteredTemplates.map((tpl) => ({
				id: tpl.id,
				title: tpl.name,
				onOpen: () => handleEdit(tpl),
				onRename: (name) => renameTemplate({ id: tpl.id, payload: { name } }),
				stats: [{ label: tpl.emailSubject || '(kein Betreff)' }],
				date: { label: 'Letzte Änderung', value: tpl.updatedAt ?? tpl.createdAt },
				actions: [
					{
						key: 'delete',
						icon: BsTrash,
						label: 'Löschen',
						danger: true,
						onClick: () => setDeleteTarget(tpl),
					},
				],
			})),
		[filteredTemplates, handleEdit, renameTemplate],
	);

	const handleDelete = useCallback(async () => {
		if (!deleteTarget) return;
		try {
			await deleteMutation.mutateAsync(deleteTarget.id);
			setDeleteTarget(null);
		} catch (err) {
			showError({ error: err, title: 'Template konnte nicht gelöscht werden' });
		}
	}, [deleteTarget, deleteMutation, showError]);

	return (
		<Page
			header={
				<PageHeader
					breadcrumbs={[{ label: 'E-Mail Kampagnen', to: '/email-campaigns' }, 'Templates']}
					title="E-Mail Templates"
					subtitle="Wiederverwendbare Vorlagen für Ihre Kampagnen"
				/>
			}
		>
			<CollectionToolbar
				search={search}
				onSearchChange={setSearch}
				searchPlaceholder="Template suchen ..."
				viewMode={viewMode}
				onViewModeChange={setViewMode}
			>
				<Button shape="pill" size="sm" onClick={handleCreate}>
					<BsPlus className="h-3.5 w-3.5" />
					Neues Template
				</Button>
			</CollectionToolbar>

			<CollectionView
				items={items}
				viewMode={viewMode}
				isLoading={isLoading}
				emptyLabel={search ? `Keine Templates für „${search}“ gefunden.` : undefined}
				createLabel="neues Template erstellen"
				onCreate={handleCreate}
			/>

			{editorOpen && (
				<EmailTemplateEditorModal
					template={editingTemplate}
					onSave={handleSave}
					onClose={() => {
						setEditorOpen(false);
						setEditingTemplate(null);
					}}
					isSaving={
						createMutation.isPending ||
						updateMutation.isPending ||
						uploadAttachmentMutation.isPending ||
						deleteAttachmentMutation.isPending
					}
				/>
			)}

			{deleteTarget && (
				<ConfirmModal
					isOpen
					onClose={() => setDeleteTarget(null)}
					onConfirm={handleDelete}
					title="Template löschen"
					message={`Möchten Sie das Template "${deleteTarget.name}" wirklich löschen?`}
					isDeleting={deleteMutation.isPending}
				/>
			)}
		</Page>
	);
};

export default EmailTemplatesPage;
